import {
  InteractionRequiredAuthError,
  type AccountInfo,
  type IPublicClientApplication,
} from '@azure/msal-browser';
import type { PdmDirectoryEntry } from '../models/pdmAssignment';

export const GRAPH_DIRECTORY_SCOPES = ['User.ReadBasic.All'];

const GRAPH_USERS_ENDPOINT = 'https://graph.microsoft.com/v1.0/users';
const MAX_RESULTS = 10;

interface GraphUser {
  displayName?: string | null;
  mail?: string | null;
  userPrincipalName?: string | null;
}

interface GraphUsersResponse {
  value?: GraphUser[];
}

export interface IUserDirectorySearch {
  searchUsers(search: string): Promise<PdmDirectoryEntry[]>;
}

export class DirectorySearchError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DirectorySearchError';
  }
}

export class GraphDirectoryService implements IUserDirectorySearch {
  private accessTokenRequest: Promise<string> | null = null;

  constructor(
    private readonly instance: IPublicClientApplication,
    private readonly account: AccountInfo,
  ) {}

  async searchUsers(search: string): Promise<PdmDirectoryEntry[]> {
    const term = search.trim();
    if (!term) return [];
    if (term.length > 100) {
      throw new DirectorySearchError('Directory search must be 100 characters or fewer.');
    }

    const accessToken = await this.getAccessToken();
    const params = new URLSearchParams({
      $select: 'displayName,mail,userPrincipalName',
      $top: String(MAX_RESULTS),
      $count: 'true',
    });
    if (term.includes('@')) {
      const escapedTerm = escapeODataString(term);
      params.set(
        '$filter',
        `startsWith(mail,'${escapedTerm}') or startsWith(userPrincipalName,'${escapedTerm}')`,
      );
    } else {
      const escapedTerm = escapeSearchTerm(term);
      params.set(
        '$search',
        `"displayName:${escapedTerm}" OR "mail:${escapedTerm}"`,
      );
    }

    let response: Response;
    try {
      response = await fetch(`${GRAPH_USERS_ENDPOINT}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          ConsistencyLevel: 'eventual',
        },
      });
    } catch {
      throw new DirectorySearchError(
        "Couldn't reach the corporate directory. Check your connection and try again.",
      );
    }

    if (!response.ok) {
      throw new DirectorySearchError(getGraphErrorMessage(response.status));
    }

    let payload: GraphUsersResponse;
    try {
      payload = (await response.json()) as GraphUsersResponse;
    } catch {
      throw new DirectorySearchError(
        'The corporate directory returned an invalid response. Please try again.',
      );
    }

    return (payload.value ?? [])
      .map((user) => ({
        pdmId: (user.mail ?? user.userPrincipalName ?? '').trim(),
        name: user.displayName?.trim() ?? '',
        email: (user.mail ?? user.userPrincipalName ?? '').trim(),
      }))
      .filter((entry) => Boolean(entry.email));
  }

  private getAccessToken(): Promise<string> {
    if (!this.accessTokenRequest) {
      this.accessTokenRequest = this.acquireAccessToken().finally(() => {
        this.accessTokenRequest = null;
      });
    }

    return this.accessTokenRequest;
  }

  private async acquireAccessToken(): Promise<string> {
    try {
      const result = await this.instance.acquireTokenSilent({
        scopes: GRAPH_DIRECTORY_SCOPES,
        account: this.account,
      });
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        try {
          const result = await this.instance.acquireTokenPopup({
            scopes: GRAPH_DIRECTORY_SCOPES,
            account: this.account,
          });
          return result.accessToken;
        } catch {
          throw new DirectorySearchError(
            'Directory access was not granted. Please try again and approve the requested permission.',
          );
        }
      }

      throw new DirectorySearchError(
        'The corporate directory could not authenticate your session. Please sign in again.',
      );
    }
  }
}

const escapeSearchTerm = (term: string): string =>
  term.split('\\').join('\\\\').split('"').join('\\"');

const escapeODataString = (term: string): string => term.split("'").join("''");

const getGraphErrorMessage = (status: number): string => {
  switch (status) {
    case 401:
      return 'Your corporate directory session has expired. Please sign in again.';
    case 403:
      return "You don't have permission to search the corporate directory.";
    case 429:
      return 'The corporate directory is receiving too many searches. Please wait and try again.';
    default:
      return 'The corporate directory is temporarily unavailable. Please try again in a moment.';
  }
};
